import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpStatusCode
} from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { ModalService, CLModalType, CLNotificationType } from '@clavisco/alerts';
import { AuthenticationService } from '../core/services/authentication.service';
import { NotificationCenterService } from '@clavisco/notification-center';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

  private readonly excludeURLs = ['token'];

  constructor(public auth: AuthenticationService,
    private modalService: ModalService,
    private notificationCenterService: NotificationCenterService
  ) { }

  /**
 * Intercepts HTTP requests and handles errors based on the HTTP status code.
 * 
 * If the error URL should be excluded, the error is ignored. Otherwise, 
 * the error is processed, and appropriate error messages are displayed in modals 
 * and notifications.
 * 
 * @param request - The HTTP request to handle.
 * @param next - The next handler to pass the request to.
 * @returns An observable containing the HTTP response or error.
 */
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(catchError((error: any) => {
      if (this.ShouldExclude(error.url)) {
        return throwError(() => error);
      }
      this.HandleError(error);
      return throwError(() => error);
    }));
  }

  /**
   * Handles the error by displaying the error message in an alert modal.
   * 
   * Based on the error status code, appropriate messages are shown to the user.
   * 
   * @param error - The error object to handle.
   */
  private HandleError(error: any): void {

    let errorMessage: string = this.GetErrorMessage(error);

    switch (error.status) {
      case 0:
        this.NotifyError('Sin conexión con el servidor', undefined, CLModalType.ERROR);
        break;
      case HttpStatusCode.Unauthorized:
        this.NotifyError('Sin autorización', 'Posiblemente debido a que la sesión es inválida, no tienes permiso para realizar esta acción o las credenciales de SAP son incorrectas', CLModalType.INFO);
        break;
      case HttpStatusCode.NotFound:
        this.NotifyError('No se encontraron registros', errorMessage, CLModalType.INFO);
        break;
      case HttpStatusCode.BadRequest:
        this.NotifyError('Solicitud incorrecta', errorMessage, CLModalType.INFO);
        break;
      case HttpStatusCode.Conflict:
        this.NotifyError('Error', errorMessage, CLModalType.ERROR);
        break;
      default:
        this.NotifyError('Error desconocido', errorMessage, CLModalType.ERROR);
    }
  }

  /**
   * Extracts and formats the error message from the error response.
   * 
   * @param pError - The error response object.
   * @returns The error message extracted from the response.
   */
  private GetErrorMessage(pError: HttpErrorResponse): string {
    if (!pError.error) return '';

    let errorMessage = pError.error.Message || pError.error.title;

    return this.FormatErrorAndDetailsMessages(errorMessage, this.MapErrorDetails(pError));
  }

  /**
   * Maps the error details and formats them as a message.
   * 
   * @param pError - The error response object containing detailed error information.
   * @returns The formatted error details message.
   */
  private MapErrorDetails(pError: HttpErrorResponse): string {
    let errorDetailsMessage = '';

    if (pError.error && pError.error.errors) {
      const errorDetails = pError.error.errors;

      const allErrors = Object.keys(errorDetails)
        .map(key => {
          return `${key}: ${errorDetails[key].join(', ')}`;
        })
        .join('\n');

      errorDetailsMessage = ` Details:\n${allErrors}`;
    }

    return errorDetailsMessage;
  }

  /**
   * Combines the main error message with the error details and formats them into a single message.
   * 
   * @param pErrorMessage - The main error message.
   * @param pErrorDetailsMessage - The error details message.
   * @returns The final formatted error message.
   */
  private FormatErrorAndDetailsMessages(pErrorMessage: string, pErrorDetailsMessage: string): string {
    return `
      \n\n${decodeURIComponent(pErrorMessage || "")}\n\n
      \n\n
      \n\n${pErrorDetailsMessage ?? 'Not available'}
    `.trim();
  }

  /**
 * Displays the error in a modal and pushes a notification to the notification center.
 * 
 * @param title - The title of the modal and notification.
 * @param subtitle - The subtitle/message for the modal and notification.
 * @param type - The type of modal (e.g., error or info).
 */
  private NotifyError(title: string, subtitle: string | undefined, type: CLModalType): void {
    this.modalService.Continue({ title, subtitle, type }).subscribe();

    this.PushNotification(title, subtitle);
  }

  /**
   * Pushes the error notification to the notification center component.
   * 
   * @param title - The title of the notification.
   * @param message - The message/subtitle for the notification.
   */
  private PushNotification(title: string | undefined, message: string | undefined): void {
    let errorMessage = message || "Unknown error";

    this.notificationCenterService.Push({
      type: CLNotificationType.ERROR,
      priority: 'high',
      title: title ?? "Error",
      message: errorMessage
    });
  }

  /**
   * Checks if the error URL should be excluded from processing.
   * 
   * @param url - The URL to check.
   * @returns A boolean indicating whether the URL is excluded from error handling.
   */
  private ShouldExclude(url: string): boolean {
    return this.excludeURLs.some(excluded => url.includes(excluded));
  }
}
